import { create } from 'zustand';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  pending?: boolean;
}

interface AiAssistantState {
  /** Last context the conversation was built against. Used to reset on nav. */
  fingerprint: string | null;
  messages: ChatMessage[];
  chips: string[];
  isStreaming: boolean;
  error: string | null;

  resetIfContextChanged: (fp: string) => void;
  appendUser: (text: string) => void;
  startAssistant: (id: string) => void;
  appendAssistantDelta: (id: string, delta: string) => void;
  finalizeAssistant: (id: string, chips: string[]) => void;
  setStreaming: (v: boolean) => void;
  setError: (m: string | null) => void;
  clearError: () => void;
  clear: () => void;
}

/**
 * Ephemeral, in-memory only. NO `persist` middleware: closing the sidebar or
 * tab loses the conversation. The fingerprint mechanism resets the chat when
 * the user navigates to a different project/event/etc.
 */
export const useAiAssistantStore = create<AiAssistantState>((set, get) => ({
  fingerprint: null,
  messages: [],
  chips: [],
  isStreaming: false,
  error: null,

  resetIfContextChanged: (fp) => {
    if (get().fingerprint === fp) return;
    set({
      fingerprint: fp,
      messages: [],
      chips: [],
      isStreaming: false,
      error: null,
    });
  },

  appendUser: (text) =>
    set((s) => ({
      messages: [
        ...s.messages,
        { id: `u-${Date.now()}`, role: 'user', content: text },
      ],
      chips: [],
      error: null,
    })),

  startAssistant: (id) =>
    set((s) => ({
      messages: [
        ...s.messages,
        { id, role: 'assistant', content: '', pending: true },
      ],
    })),

  appendAssistantDelta: (id, delta) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === id ? { ...m, content: m.content + delta } : m,
      ),
    })),

  finalizeAssistant: (id, chips) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === id ? { ...m, pending: false } : m,
      ),
      chips,
    })),

  setStreaming: (v) => set({ isStreaming: v }),
  setError: (m) =>
    set((s) => ({
      // Drop any empty pending assistant bubbles when an error lands so the
      // user doesn't see the three-dot "thinking" animation stuck forever
      // next to the error banner. Bubbles with partial content are kept so
      // we don't lose tokens that arrived before the failure.
      messages: s.messages.filter(
        (msg) => !(msg.pending && msg.content.length === 0),
      ),
      error: m,
      isStreaming: false,
    })),
  /** Wipe the error flag without touching messages, so the user can retry. */
  clearError: () => set({ error: null }),
  clear: () =>
    set({ messages: [], chips: [], isStreaming: false, error: null }),
}));
